import { useEffect, useState } from 'react';

export default function JobOrderProforma() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {    
        const storedData = localStorage.getItem('invoice');
        
        if (!storedData) {
          setError("No invoice data found in local storage");
          setLoading(false);
          return;
        }
        
        const parsedData = JSON.parse(storedData);
        setData(parsedData);    
    } catch (error) {
      console.error("Error parsing data:", error);    
      setError("Failed to load invoice data. Please try again.");
    }
    
    setLoading(false);
  }, []);
  
  const handlePrint = () => {
    window.print();
  };
  
  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }
  
  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  }
  
  if (!data) {
    return <div className="flex justify-center items-center h-screen">No data available</div>;
  }
  
  // Safe access function for nested properties
  const safeGet = (obj, path, defaultValue = "") => {
    try {
      const result = path.split('.').reduce((o, key) => (o && o[key] !== undefined && o[key] !== null) ? o[key] : null, obj);
      return (result !== undefined && result !== null) ? result : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  };
  
  // Format date safely
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return !isNaN(date.getTime()) ? date.toLocaleDateString('en-IN') : "";
    } catch (e) {
      return "";
    }
  };
  
  // Get products array safely
  const products = Array.isArray(data.products) ? data.products : [];
  
  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="mb-4 print:hidden">
        <button 
          onClick={handlePrint}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Print Form
        </button>
      </div>
      
      {/* A4 container with proper scaling for print */}
      <div className="border border-black print:m-0 print:w-full print:h-full" style={{ pageBreakAfter: 'always' }}>
        {/* Header with company info (two identical columns) */}
        <div className="flex border-b border-black">
          <div className="w-1/2 border-r border-black p-1 text-center flex flex-col justify-center">
            <div className="text-xs">
              <p className="font-bold">From,</p>
              <p className="font-bold">Printverse Pvt.Ltd.</p>
              <p>20 Jura Tukoganj, Opp. Ramlagh Petrol Pump.</p>
              <p>Indore (M.P.) - 452005</p>
              <p>Contact - 7389825004, 7038231237</p>
            </div>
          </div>
          <div className="w-1/2 p-1 text-center flex flex-col justify-center">
            <div className="text-xs">
              <p className="font-bold">To,</p>
              <p className="font-bold">{safeGet(data, 'leadId.name')}</p>
              <p>{safeGet(data, 'flat')}{safeGet(data, 'flat') && safeGet(data, 'area') ? ", " : ""}{safeGet(data, 'area')}</p>
              <p>
                {safeGet(data, 'city')}{safeGet(data, 'city') && safeGet(data, 'pincode') ? ", " : ""}{safeGet(data, 'pincode')}
              </p>
              <p>Contact - {safeGet(data, 'primaryPhone')}</p>
            </div>
          </div>
        </div>
        
        {/* Job Order Title Row */}
        <div className="flex border-b border-black">
          <div className="w-3/4 p-1 border-r border-black">
            <p className="text-lg font-bold">Job Order Proforma (PI No.: {safeGet(data, '_id', '').substring(0, 6)})</p>
          </div>
          <div className="w-1/4 p-1 flex items-center">
            <span className="mr-1 text-xs">Priority:</span>
            <div className="text-xs">{safeGet(data, 'orderPriority')}</div>
          </div>
        </div>
        
        {/* Order Details Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/4 p-1 border-r border-black">
            <p><strong>Order No.:</strong> {safeGet(data, '_id', '')}</p>
          </div>
          <div className="w-1/4 p-1 border-r border-black">
            <p><strong>Order Date:</strong> {formatDate(safeGet(data, 'createdAt'))}</p>
          </div>
          <div className="w-1/4 p-1 border-r border-black">
            <p><strong>Dispatch Date:</strong> {formatDate(safeGet(data, 'dispatchDate'))}</p>
          </div>
          <div className="w-1/4 p-1">
            <p><strong>Order By:</strong> {safeGet(data, 'leadId.name')}</p>
          </div>
        </div>
        
        {/* Customer Name Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/2 p-1 border-r border-black">
            <p><strong>Customer Name:</strong> {safeGet(data, 'leadId.name')}</p>
          </div>
          <div className="w-1/2 p-1">
            <p><strong>Product:</strong></p>
          </div>
        </div>
        
        {/* Products Section - for multiple products */}
        <div className="border-b border-black">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-black">
                <th className="border-r border-black p-1 w-8 text-center">Sr No.</th>
                <th className="border-r border-black p-1 w-1/5">Design</th>
                <th className="border-r border-black p-1 w-1/5">Input Q.</th>
                <th className="border-r border-black p-1 w-1/5">Final Dam.</th>
                <th className="p-1 w-1/5">Final Q.</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => (
                <tr key={index} className={index < products.length - 1 ? "border-b border-black" : ""}>
                  <td className="border-r border-black p-1 text-center">{index + 1}.</td>
                  <td className="border-r border-black p-1">
                    <p><strong>Code:</strong> {safeGet(product, 'productCode')}</p>
                    <p><strong>Size:</strong> {safeGet(product, 'size')}</p>
                    <p><strong>Q:</strong> {safeGet(product, 'quantity')}</p>
                  </td>
                  <td className="border-r border-black p-1"></td>
                  <td className="border-r border-black p-1"></td>
                  <td className="p-1"></td>
                </tr>
              ))}
              {/* Add empty rows if less than 3 products - reduced from 5 to save space */}
              {Array.from({ length: Math.max(0, 3 - products.length) }).map((_, index) => (
                <tr key={`empty-${index}`} className={index < 2 - products.length ? "border-b border-black" : ""}>
                  <td className="border-r border-black p-1 text-center">{products.length + index + 1}.</td>
                  <td className="border-r border-black p-1"></td>
                  <td className="border-r border-black p-1"></td>
                  <td className="border-r border-black p-1"></td>
                  <td className="p-1"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Print File and Cut File Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/2 p-1 border-r border-black">
            <p><strong>Print File:</strong></p>
          </div>
          <div className="w-1/2 p-1">
            <p><strong>Cut File:</strong></p>
          </div>
        </div>
        
        {/* Dispatch Mode and Packed By Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/2 p-1 border-r border-black">
            <p><strong>Disp. Mode:</strong> {safeGet(data, 'dispatchMode')}</p>
          </div>
          <div className="w-1/2 p-1">
            <p><strong>Packed by:</strong></p>
          </div>
        </div>
        
        {/* Weight/Box and Package Date Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/2 p-1 border-r border-black">
            <p><strong>Wgt/Box:</strong></p>
          </div>
          <div className="w-1/2 p-1">
            <p><strong>Pkg Date:</strong></p>
          </div>
        </div>
        
        {/* Special Instructions Row */}
        <div className="flex border-b border-black text-xs">
          <div className="w-1/4 p-1 border-r border-black">
            <p><strong>Special Instructions</strong></p>
          </div>
          <div className="w-3/4">
            <table className="w-full border-collapse">
              <tbody>
                {[1, 2, 3].map((num) => (
                  <tr key={num} className={num < 3 ? "border-b border-black" : ""}>
                    <td className="border-r border-black p-1 w-8 text-center">{num}.</td>
                    <td className="p-1">&nbsp;</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Department tracking table */}
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-black">
              <th className="border-r border-black p-1 w-1/6">Department</th>
              <th className="border-r border-black p-1 w-1/6">Person</th>
              <th className="border-r border-black p-1 w-1/6">Start Date & Time</th>
              <th className="border-r border-black p-1 w-1/6">End Date & Time</th>
              <th className="border-r border-black p-1 w-1/6">Input/Damage Q.</th>
              <th className="p-1 w-1/6">Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-black">
              <td className="border-r border-black p-1 font-bold">Design</td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="p-1"></td>
            </tr>
            <tr className="border-b border-black">
              <td className="border-r border-black p-1 font-bold">Machine</td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="p-1"></td>
            </tr>
            <tr className="border-b border-black">
              <td className="border-r border-black p-1 font-bold">Production</td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="p-1"></td>
            </tr>
            <tr>
              <td className="border-r border-black p-1 font-bold">Packing</td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="border-r border-black p-1"></td>
              <td className="p-1"></td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <style jsx global>{`
        @page {
          size: A4;
          margin: 0;
        }
        
        @media print {
          html, body {
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 0;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .print\\:w-full {
            width: 100%;
          }
          
          .print\\:h-full {
            height: 100%;
          }
          
          .print\\:m-0 {
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
}